import { apiClient } from './client'
import type { ApiResponse } from './types'

interface Envelope<T> {
  success: boolean
  data: T
  error?: string
}

/**
 * Unwrap the backend `{success, data, error?}` envelope. Throws on
 * `success: false` so the failure surfaces (React Query `isError`) instead of
 * silently resolving to `undefined` data.
 */
function unwrap<T>(res: Envelope<T>): T {
  if (!res?.success) {
    throw new Error(res?.error || 'Request failed')
  }
  return res.data
}

export interface ImpersonateableUser {
  id: string
  email: string
  firstName: string
  lastName: string
  userType: string
  status: string
  createdAt: string
}

export interface ImpersonationStartResponse {
  token: string
  impersonatedUser: {
    id: string
    email: string
    firstName: string
    lastName: string
    userType: string
    roles: string[]
  }
  // Backend returns an `ImpersonationSession` here; callers don't use it yet
  // so we leave it loose rather than import the full snake_case shape.
  session?: Record<string, unknown>
  // Optional: backend may surface the admin who initiated impersonation so
  // the UI can render a "Return to admin" affordance. Not always set.
  originalAdmin?: { email: string }
}

interface ImpersonationStopResponse {
  token: string
  user: {
    id: string
    email: string
    firstName: string
    lastName: string
    userType: string
    roles: string[]
  }
  session?: Record<string, unknown>
}

export interface ImpersonationHistoryResult {
  sessions: Array<{
    id: string
    target_user_id: string
    reason: string
    started_at: string
    ended_at?: string
    expires_at: string
    status: string
    target_email: string
    target_first_name: string
    target_last_name: string
    target_role: string
  }>
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

interface ImpersonationStatusResponse {
  session: {
    id: string
    target_user_id: string
    reason: string
    started_at: string
    expires_at: string
    status: string
    targetUser: {
      id: string
      email: string
      firstName: string
      lastName: string
      role: string
    }
  } | null
}

export const impersonationApi = {
  getImpersonateableUsers: (params?: {
    search?: string
    page?: number
    limit?: number
  }): Promise<ApiResponse<{ users: ImpersonateableUser[] }>> =>
    apiClient.get('/users/impersonation/admin/users', { params }),

  startImpersonation: async (
    userId: string,
    reason?: string
  ): Promise<ImpersonationStartResponse> => {
    const response = await apiClient.post<{ success: boolean; data: ImpersonationStartResponse }>(
      '/users/impersonation/admin/start',
      { targetUserId: userId, reason: reason || 'Admin impersonation' }
    )
    return unwrap(response as unknown as Envelope<ImpersonationStartResponse>)
  },

  stopImpersonation: async (): Promise<ImpersonationStopResponse> => {
    const response = await apiClient.post<{ success: boolean; data: ImpersonationStopResponse }>(
      '/users/impersonation/admin/stop'
    )
    return unwrap(response as unknown as Envelope<ImpersonationStopResponse>)
  },

  getImpersonationStatus: async (): Promise<ImpersonationStatusResponse> => {
    const response = await apiClient.get<{ success: boolean; data: ImpersonationStatusResponse }>(
      '/users/impersonation/status'
    )
    return unwrap(response as unknown as Envelope<ImpersonationStatusResponse>)
  },

  getImpersonationHistory: (params: URLSearchParams): Promise<ApiResponse<ImpersonationHistoryResult>> =>
    apiClient.get(`/users/impersonation/history?${params.toString()}`),

  // #6 (design-review-2026-05): platform-wide aggregate KPIs for the
  // admin /impersonation page tiles. Replaces the prior approach of
  // computing Total / Active / ThisMonth from getImpersonationSessions(),
  // which only returns the caller's currently-active sessions and
  // therefore always read 0.
  getImpersonationStats: (): Promise<ApiResponse<{
    total: number
    active: number
    thisMonth: number
  }>> =>
    apiClient.get('/users/impersonation/stats'),

  /**
   * Calling admin's recently-impersonated targets (MRU). Powers the
   * top-right impersonate-menu shortcut list — click an entry to
   * re-impersonate without opening the full picker modal.
   */
  getRecentImpersonations: (
    limit: number = 5,
  ): Promise<ApiResponse<{ users: Array<{
    id: string
    email: string
    firstName: string | null
    lastName: string | null
    userType: string
    lastImpersonatedAt: string
  }> }>> =>
    apiClient.get('/users/impersonation/recent', { params: { limit } }),
}
